import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';

function getStatusConfig(theme, status) {
  const { palette } = theme;
  const statusLower = status?.toLowerCase() || '';

  switch (statusLower) {
    case 'active':
    case 'success':
    case 'completed':
      return {
        color: palette.success.dark,
        bgColor: palette.success.light,
        borderColor: alpha(palette.success.main, 0.4),
        label: 'Active',
        icon: CheckCircleIcon,
        pulse: true,
      };
    case 'failed':
    case 'error':
      return {
        color: palette.error.dark,
        bgColor: palette.error.light,
        borderColor: alpha(palette.error.main, 0.35),
        label: 'Failed',
        icon: ErrorIcon,
        pulse: false,
      };
    case 'paused':
    case 'inactive':
      return {
        color: palette.warning.dark,
        bgColor: palette.warning.light,
        borderColor: alpha(palette.warning.main, 0.4),
        label: 'Paused',
        icon: PauseIcon,
        pulse: false,
      };
    case 'pending':
    case 'queued':
      return {
        color: palette.info.main,
        bgColor: palette.info.light,
        borderColor: alpha(palette.info.main, 0.35),
        label: 'Pending',
        icon: ScheduleIcon,
        pulse: false,
      };
    case 'running':
    case 'in_progress':
    case 'processing':
      return {
        color: palette.primary.dark,
        bgColor: palette.primary.light,
        borderColor: alpha(palette.primary.main, 0.35),
        label: 'Running',
        icon: PlayArrowIcon,
        pulse: true,
      };
    default:
      return {
        color: palette.text.secondary,
        bgColor: palette.background.default,
        borderColor: palette.divider,
        label: status || 'Unknown',
        icon: HelpIcon,
        pulse: false,
      };
  }
}

/**
 * StatusChip — status indicator using theme palette only.
 */
const StatusChip = ({ status, size = 'medium', showLabel = true }) => {
  const theme = useTheme();
  const config = getStatusConfig(theme, status);
  const Icon = config.icon;

  const sizes = {
    small: {
      height: 24,
      iconSize: 14,
      fontSize: theme.typography.caption.fontSize,
      px: 1.25,
      gap: 0.5,
    },
    medium: {
      height: 28,
      iconSize: 16,
      fontSize: theme.typography.body2.fontSize,
      px: 1.5,
      gap: 0.75,
    },
    large: {
      height: 32,
      iconSize: 18,
      fontSize: theme.typography.body1.fontSize,
      px: 2,
      gap: 1,
    },
  };

  const currentSize = sizes[size] || sizes.medium;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: currentSize.gap,
        px: currentSize.px,
        height: currentSize.height,
        borderRadius: 2,
        backgroundColor: config.bgColor,
        border: `1px solid ${config.borderColor}`,
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: `0 2px 8px ${alpha(config.color, 0.2)}`,
        },
      }}
    >
      {config.pulse && (
        <Box
          sx={{
            position: 'absolute',
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: config.color,
            left: currentSize.px * 8 + 4,
            animation: 'statusPulse 2s infinite',
            '@keyframes statusPulse': {
              '0%': { boxShadow: `0 0 0 0 ${alpha(config.color, 0.7)}` },
              '70%': { boxShadow: `0 0 0 6px ${alpha(config.color, 0)}` },
              '100%': { boxShadow: `0 0 0 0 ${alpha(config.color, 0)}` },
            },
          }}
        />
      )}

      <Icon
        sx={{
          fontSize: currentSize.iconSize,
          color: config.color,
          ml: config.pulse ? 1.5 : 0,
        }}
      />

      {showLabel && (
        <Typography
          sx={{
            color: config.color,
            fontSize: currentSize.fontSize,
            fontWeight: theme.typography.h6.fontWeight,
            lineHeight: 1,
          }}
        >
          {config.label}
        </Typography>
      )}
    </Box>
  );
};

export default StatusChip;
